/**
 * Public “Join the Test” landing — invite-only waitlist, no auth required.
 * Path: /join  (aliases: /join-the-test)
 */

import * as api from "./api.js";
import { clear, el } from "./dom.js";
import { ApiError } from "./types.js";

export function isJoinPath(pathname: string = window.location.pathname): boolean {
  const p = pathname.replace(/\/+$/, "") || "/";
  return p === "/join" || p === "/join-the-test";
}

export function mountJoinPage(container: HTMLElement): void {
  renderJoin(container);
}

function renderJoin(root: HTMLElement, opts?: {
  busy?: boolean;
  error?: string | null;
  done?: boolean;
  message?: string | null;
}): void {
  clear(root);
  const busy = opts?.busy === true;
  const done = opts?.done === true;

  const page = el("div", { className: "join-page" });

  // Top bar
  const top = el("header", { className: "join-topbar" });
  top.append(
    el(
      "a",
      { href: "/", className: "brand join-brand brand-link" },
      el("img", {
        className: "brand-mark",
        src: "/favicon.svg",
        alt: "",
        width: "28",
        height: "28",
        decoding: "async",
      }),
      el("span", null, "MCP to AI"),
    ),
    el(
      "div",
      { className: "join-top-actions" },
      el("a", { href: "/", className: "btn-ghost btn-sm" }, "Sign in"),
    ),
  );
  page.append(top);

  // Hero
  const hero = el("section", { className: "join-hero" });
  hero.append(
    el("p", { className: "join-kicker" }, "Closed test · invite only"),
    el(
      "h1",
      { className: "join-title" },
      "Email that your AI can actually use",
    ),
    el(
      "p",
      { className: "join-lede" },
      "MCP to AI connects Claude, Grok, and other MCP clients to your multi-provider AI accounts — without pasting API keys into chat.",
    ),
  );
  page.append(hero);

  // Features grid
  const features = el("section", { className: "join-features" });
  const items: Array<{ title: string; body: string }> = [
    {
      title: "MCP-native",
      body: "Works with Claude, Cursor, Grok, and other MCP hosts via OAuth or a personal access token.",
    },
    {
      title: "Gmail + IMAP",
      body: "Connect Gmail with OAuth, or iCloud / Outlook / Yahoo / generic IMAP with app passwords.",
    },
    {
      title: "Beyond ai",
      body: "Optional Google Contacts, Calendar, and Gmail settings tools when you enable them.",
    },
    {
      title: "Invite-only",
      body: "No public signup. We keep the cohort small while Google OAuth stays in Testing mode.",
    },
  ];
  for (const it of items) {
    features.append(
      el(
        "article",
        { className: "join-card" },
        el("h3", null, it.title),
        el("p", null, it.body),
      ),
    );
  }
  page.append(features);

  // Form card
  const card = el("section", { className: "join-form-card panel" });
  card.append(
    el("h2", null, "Request an invite"),
    el(
      "p",
      { className: "sub" },
      "Tell us who you are. If you’re a fit for the closed test, we’ll email a personal invite link from noreply@mcpto.ai.",
    ),
  );

  if (done) {
    card.append(
      el(
        "div",
        { className: "alert alert-ok", role: "status" },
        opts?.message ||
          "Thanks — if you are a fit for the closed test, we will email you an invite.",
      ),
      el(
        "p",
        { className: "hint" },
        "Already invited? ",
        el("a", { href: "/" }, "Sign in"),
        " with the email we used.",
      ),
    );
  } else {
    if (opts?.error) {
      card.append(el("div", { className: "alert alert-error", role: "alert" }, opts.error));
    }

    const form = el("form", {
      className: "form form-stack join-form",
      novalidate: "true",
    });

    const email = el("input", {
      type: "email",
      name: "email",
      className: "input",
      required: "true",
      autocomplete: "email",
      placeholder: "you@example.com",
    }) as HTMLInputElement;

    const name = el("input", {
      type: "text",
      name: "name",
      className: "input",
      autocomplete: "name",
      placeholder: "Optional",
      maxlength: "100",
    }) as HTMLInputElement;

    const company = el("input", {
      type: "text",
      name: "company",
      className: "input",
      autocomplete: "organization",
      placeholder: "Optional",
      maxlength: "120",
    }) as HTMLInputElement;

    const note = el("textarea", {
      name: "note",
      className: "input",
      rows: "3",
      placeholder: "How do you plan to use mcpto.ai? (optional)",
      maxlength: "800",
    }) as HTMLTextAreaElement;

    // Gmail OAuth question (required for Google Test users ops)
    const gmailFieldset = el("fieldset", { className: "join-gmail-fieldset" });
    gmailFieldset.append(
      el(
        "legend",
        null,
        "Will you connect a Gmail / Google account? *",
      ),
      el(
        "p",
        { className: "hint" },
        "Connect Gmail uses Google OAuth. While we are in Google Testing mode, every Gmail address must be on our test-user list.",
      ),
    );
    const gmailYes = el("input", {
      type: "radio",
      name: "wantsGmail",
      value: "yes",
      id: "wants-gmail-yes",
      required: "true",
    }) as HTMLInputElement;
    const gmailNo = el("input", {
      type: "radio",
      name: "wantsGmail",
      value: "no",
      id: "wants-gmail-no",
    }) as HTMLInputElement;
    const gmailChoices = el("div", { className: "join-gmail-choices" });
    gmailChoices.append(
      el(
        "label",
        { className: "join-radio" },
        gmailYes,
        " Yes — I'll use Connect Gmail",
      ),
      el(
        "label",
        { className: "join-radio" },
        gmailNo,
        " No — IMAP only (iCloud, Outlook, etc.)",
      ),
    );
    gmailFieldset.append(gmailChoices);

    const gmailEmailWrap = el("div", {
      className: "join-gmail-email-wrap",
      hidden: "true",
    });
    const gmailEmail = el("input", {
      type: "email",
      name: "gmailEmail",
      className: "input",
      autocomplete: "email",
      placeholder: "Same as above, or a different Google account",
    }) as HTMLInputElement;
    gmailEmailWrap.append(
      el(
        "label",
        { className: "field" },
        "Google account email (if different from login email)",
        gmailEmail,
      ),
    );
    gmailFieldset.append(gmailEmailWrap);

    const syncGmailUi = () => {
      const yes = gmailYes.checked;
      if (yes) {
        gmailEmailWrap.removeAttribute("hidden");
        if (!gmailEmail.value.trim() && email.value.trim()) {
          gmailEmail.placeholder = email.value.trim();
        }
      } else {
        gmailEmailWrap.setAttribute("hidden", "true");
      }
    };
    gmailYes.addEventListener("change", syncGmailUi);
    gmailNo.addEventListener("change", syncGmailUi);
    email.addEventListener("blur", () => {
      // Default Gmail yes when address looks like gmail.com
      const e = email.value.trim().toLowerCase();
      if (
        (e.endsWith("@gmail.com") || e.endsWith("@googlemail.com")) &&
        !gmailYes.checked &&
        !gmailNo.checked
      ) {
        gmailYes.checked = true;
        syncGmailUi();
      }
    });

    // Honeypot — hidden from users, bots often fill it
    const website = el("input", {
      type: "text",
      name: "website",
      className: "join-hp",
      tabindex: "-1",
      autocomplete: "off",
      "aria-hidden": "true",
    }) as HTMLInputElement;

    form.append(
      el("label", { className: "field" }, "Email for sign-in *", email),
      el(
        "div",
        { className: "form-row" },
        el("label", { className: "field" }, "Name", name),
        el("label", { className: "field" }, "Company", company),
      ),
      gmailFieldset,
      el("label", { className: "field" }, "Note", note),
      website,
      el(
        "div",
        { className: "form-actions" },
        el(
          "button",
          {
            type: "submit",
            className: "btn-primary",
            disabled: busy ? true : undefined,
          },
          busy ? "Sending…" : "Request access",
        ),
      ),
      el(
        "p",
        { className: "hint join-legal" },
        "By requesting access you agree to our ",
        el("a", { href: "/privacy" }, "Privacy"),
        " and ",
        el("a", { href: "/terms" }, "Terms"),
        " placeholders. This is not open self-signup.",
      ),
    );

    form.addEventListener("submit", (ev) => {
      ev.preventDefault();
      if (!gmailYes.checked && !gmailNo.checked) {
        renderJoin(root, {
          error: "Please say whether you will connect Gmail.",
        });
        return;
      }
      void onSubmit(root, {
        email: email.value.trim(),
        name: name.value.trim(),
        company: company.value.trim(),
        note: note.value.trim(),
        website: website.value,
        wantsGmail: gmailYes.checked,
        gmailEmail: gmailEmail.value.trim(),
      });
    });
    card.append(form);
  }
  page.append(card);

  // Footer
  page.append(
    el(
      "footer",
      { className: "join-footer" },
      el("a", { href: "/docs" }, "Setup docs"),
      el("span", { className: "join-dot" }, "·"),
      el("a", { href: "/privacy" }, "Privacy"),
      el("span", { className: "join-dot" }, "·"),
      el("a", { href: "/terms" }, "Terms"),
      el("span", { className: "join-dot" }, "·"),
      el("a", { href: "/" }, "Sign in"),
    ),
  );

  root.append(page);
}

async function onSubmit(
  root: HTMLElement,
  input: {
    email: string;
    name: string;
    company: string;
    note: string;
    website: string;
    wantsGmail: boolean;
    gmailEmail: string;
  },
): Promise<void> {
  if (!input.email) {
    renderJoin(root, { error: "Email is required." });
    return;
  }
  renderJoin(root, { busy: true });
  try {
    const res = await api.submitJoinRequest({
      email: input.email,
      wantsGmail: input.wantsGmail,
      ...(input.wantsGmail && input.gmailEmail
        ? { gmailEmail: input.gmailEmail }
        : {}),
      ...(input.name ? { name: input.name } : {}),
      ...(input.company ? { company: input.company } : {}),
      ...(input.note ? { note: input.note } : {}),
      ...(input.website ? { website: input.website } : {}),
    });
    renderJoin(root, {
      done: true,
      message: res.message,
    });
  } catch (err) {
    const msg =
      err instanceof ApiError
        ? err.message
        : err instanceof Error
          ? err.message
          : "Could not submit request";
    renderJoin(root, { error: msg });
  }
}
